import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

import { getAnnotationColor } from '../utils/annotationPresentation';
import { getAnnotationTitleDisplay, getAnnotationTypeLabel } from '../utils/annotationDescriptionDisplay';
import { PopoverItem, ColorDot } from './AnnotationClusterMarker.styled';
import {
  PopoverCard,
  PopoverHeader,
  RowTitle,
  RowRange,
} from './RangeClusterPopover.styled';

// Date-only span, e.g. "Jun 5 → Jun 10", matching the picker design.
function formatRange(a) {
  const start = moment(a.start_time).format('MMM D');
  if (!a.end_time || a.end_time === a.start_time) return start;
  return `${start} → ${moment(a.end_time).format('MMM D')}`;
}

// Floating list of the annotations inside an overlap zone, opened when the zone's
// ×N badge is clicked (on either the D3 trend chart or the DOM overlay).
function RangeClusterPopover(props) {
  const { items, type, position, onSelect, onClose, onHoverItem, onLeaveItem } = props;
  const { t } = useTranslation();
  const ref = useRef(null);

  // Header reads e.g. "4 ALARMS OVERLAPPING HERE"; derive the type from the
  // explicit prop or the first item (overlap zones are single-type).
  const headerType = type || (items[0] && items[0].type);
  const typeLabel = headerType ? getAnnotationTypeLabel(t, headerType) : '';

  useEffect(() => {
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    // Escape closes; Tab is trapped within the popover so keyboard/AT users
    // can cycle the rows instead of tabbing straight back out to the chart.
    const onKey = (e) => {
      if (e.key === 'Escape') { onClose(); return; }
      if (e.key !== 'Tab' || !ref.current) return;
      const rows = Array.prototype.slice.call(
        ref.current.querySelectorAll('[role="menuitem"]')
      );
      if (!rows.length) return;
      const first = rows[0];
      const last = rows[rows.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('mousedown', onDocClick, true);
    document.addEventListener('keydown', onKey, true);
    // Move focus onto the first row on open so the list is immediately reachable.
    const firstRow = ref.current && ref.current.querySelector('[role="menuitem"]');
    if (firstRow) firstRow.focus();
    return () => {
      document.removeEventListener('mousedown', onDocClick, true);
      document.removeEventListener('keydown', onKey, true);
      // Drop any region highlight when the popover unmounts.
      if (onLeaveItem) onLeaveItem();
    };
  }, [onClose, onLeaveItem]);

  const sorted = useMemo(
    () => [...items].sort((a, b) => new Date(a.start_time) - new Date(b.start_time)),
    [items]
  );

  const handleSelect = useCallback((a, e) => {
    e.stopPropagation();
    onSelect(a);
  }, [onSelect]);

  // Portal to body so the popover escapes the chart's stacking context (the
  // annotation band uses `isolation: isolate`, which would otherwise pin the
  // popover below the trend line drawn in a sibling chart container).
  return ReactDOM.createPortal(
    <PopoverCard
      innerRef={(node) => { ref.current = node; }}
      style={{ left: position.x, top: position.y }}
      role="menu"
      onMouseLeave={() => { if (onLeaveItem) onLeaveItem(); }}
    >
      <PopoverHeader>
        {t('annotations.cluster.overlapLabel', {
          defaultValue: '{{count}} {{type}} overlapping here',
          count: items.length,
          type: typeLabel
        })}
      </PopoverHeader>
      {sorted.map(a => (
        <PopoverItem
          key={a.id}
          // Native <button>: already tab-focusable and fires click on Enter/Space,
          // so no explicit tabIndex or Enter/Space keydown handler is needed. The
          // role="menuitem" (for the role="menu" container) and the parent's Tab
          // trap are what add value here.
          role="menuitem"
          onClick={e => handleSelect(a, e)}
          onMouseEnter={() => { if (onHoverItem) onHoverItem(a); }}
          onFocus={() => { if (onHoverItem) onHoverItem(a); }}
        >
          <ColorDot color={getAnnotationColor(a, { respectSeverity: false })} />
          <RowTitle>{getAnnotationTitleDisplay(t, a)}</RowTitle>
          <RowRange>{formatRange(a)}</RowRange>
        </PopoverItem>
      ))}
    </PopoverCard>,
    document.body
  );
}

RangeClusterPopover.propTypes = {
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
  type: PropTypes.string,
  position: PropTypes.shape({
    x: PropTypes.number.isRequired,
    y: PropTypes.number.isRequired
  }).isRequired,
  onSelect: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onHoverItem: PropTypes.func,
  onLeaveItem: PropTypes.func
};

RangeClusterPopover.defaultProps = {
  type: null,
  onHoverItem: null,
  onLeaveItem: null
};

export default RangeClusterPopover;
